import {
  getNumberOfHouseholdsInPolygonBulk,
  searchFeaturesBulk,
} from '../../Module/graphQL/PathFinder/pathFinderGQL'
import {
  densityInPolygon,
  isPathFinderSupportsCountry,
} from '../../shared/libraries/geo'
import { getFeatureFromOverlay } from '../../Containers/BuilderMap/util'

export const middlePoint = googleInstance => (
  latitude1,
  longitude1,
  latitude2,
  longitude2,
) => {
  const firstMiddlePoint = new googleInstance.maps.LatLng(latitude1, longitude1)
  const twiceMiddlePoint = new googleInstance.maps.LatLng(latitude1, longitude2)
  const { lat, lng } = googleInstance.maps.geometry.spherical.interpolate(
    firstMiddlePoint,
    twiceMiddlePoint,
    0.5,
  )

  return [lat(), lng()]
}

/**
 * Checks if it is possible to get households for the country and if yes
 * returns households and density for the received polygons
 *
 * @param {Array<Objects>} param.boundaries
 * @param {String} param.countryCode
 */
export const getNumberOfHouseholdsAndDensityForBoundaries = async ({
  boundaries,
  countryCode,
}) => {
  if (!isPathFinderSupportsCountry(countryCode) || boundaries.length <= 0) {
    return [{}]
  }

  const geometries = boundaries.map(boundary => ({
    type: 'Polygon',
    coordinates: [
      [
        ...boundary.map(({ latitude, longitude }) => [longitude, latitude]),
        // First and last coords should be the same in GEOJSON
        [boundary[0].longitude, boundary[0].latitude],
      ],
    ],
  }))

  const households = await getNumberOfHouseholdsInPolygonBulk(
    countryCode,
    geometries,
  )

  return boundaries.map((boundary, index) => {
    const householdsAmount = households[index]
    return {
      householdsAmount,
      density: densityInPolygon({ polygon: boundary, householdsAmount }),
    }
  })
}

/**
 * Gets the number of households in each geometry. If Pathfinder does not
 * support the country then null is returned as the value
 *
 * @param {Array<GeoJSONPolygon>} param.geometries
 * @param {String} param.countryCode
 * @return {Array<number|null>}
 */
export const getNumberOfHouseholdsBulk = async ({
  geometries,
  countryCode,
}) => {
  return await getNumberOfHouseholdsInPolygonBulk(countryCode, geometries)
}

export const getDensity = geometry => {}

/**
 * Checks if it is possible to get postcodes for the country and if yes
 * returns the first matching postcode for the received polygons
 *
 * @param {Array<Objects>} param.boundaries
 * @param {String} param.countryCode
 * @param {Promise<Array<string|null>>}
 */
export const getPostcodesForBoundaries = async ({
  geometries,
  countryCode,
}) => {
  if (!geometries || !geometries.length) return []

  const chunkSize = 250
  const chunks = Math.ceil(geometries.length / chunkSize)
  const promises = []

  for (let i = 0; i < chunks; i++) {
    promises.push(
      new Promise(async (resolve, reject) => {
        try {
          const res = await searchFeaturesBulk({
            countryCode,
            geometries: geometries.slice(i * chunkSize, (i + 1) * chunkSize),
            layer: 'Postcodes',
            includeAddressCount: false,
          })

          resolve(res)
        } catch (e) {
          reject(e)
        }
      }),
    )
  }

  const result = (await Promise.all(promises)).reduce((acc, res) => {
    acc.push(...res)
    return acc
  }, [])

  return geometries.map((boundary, index) => {
    var postcodes =
      result[index] && result[index].collection.features
        ? result[index].collection.features
        : []
    return postcodes[0] ? postcodes[0].properties.name : null
  })
}

export const createOverlayName = async ({
  countryCode,
  boundary,
  renderedOverlays = [],
}) => {
  try {
    const { geometry } = getFeatureFromOverlay({ countryCode, boundary })

    const relatedPostcodesData = await getPostcodesForBoundaries({
      geometries: [geometry],
      countryCode,
    })

    let generatedName = `${countryCode}-${(relatedPostcodesData &&
      relatedPostcodesData[0]) ||
      'Unnamed_area'}`

    let uniqueName = generatedName
    let duplicatesAmount = 0

    // Generate new name by appending index to the end until there is no dups
    while (
      renderedOverlays.find(({ name, label }) =>
        [name, label].includes(uniqueName),
      )
    ) {
      duplicatesAmount += 1
      uniqueName = `${generatedName}#${duplicatesAmount}`
    }

    return uniqueName
  } catch (error) {
    console.error(error)
    return 'Unnamed_area'
  }
}



// WEBPACK FOOTER //
// ./src/Utils/Map/index.js